import { useState } from "react";
import AnimateHeight from "react-animate-height";

import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import ChevronRight from "atoms/icon/icons/chevron_right.svg";
import CheckIcon from "atoms/icon/icons/ic_added_to_cart_check_ol.svg";
import { AccordionList } from "molecules/accordion-list/AccordionList";

const DESCRIPTION_LENGTH_LIMIT = 200;

export interface RetailProductDetailsSectionProps {
  keyBenefits: Array<{ text: string }>;
  description: { text: string };
  areas: Array<{ id: string; name?: string | null }>;
  concerns: Array<{ id: string; name?: string | null }>;
  areaIds: string[];
  concernIds: string[];
  defaultIngredients: Array<{ text: string }>;
}

export const RetailProductDetailsSection = ({
  keyBenefits,
  description,
  areas,
  concerns,
  areaIds,
  concernIds,
  defaultIngredients,
}: RetailProductDetailsSectionProps) => {
  const [showFullDescription, setShowFullDescription] = useState(false);

  const getFilteredNames = (
    items: Array<{ id: string; name?: string | null }>,
    ids: string[]
  ) => {
    return (
      items
        ?.filter((item) => ids.includes(item.id))
        .map((item) => item.name ?? "")
        .filter(Boolean)
        .join(", ") ?? ""
    );
  };

  const productAreas = getFilteredNames(areas, areaIds);
  const productConcerns = getFilteredNames(concerns, concernIds);
  const ingredientsText = defaultIngredients
    .map((ingredient) => ingredient.text)
    .join(", ");

  const needsTruncation = description.text.length > DESCRIPTION_LENGTH_LIMIT;

  const getDescriptionHeight = () => {
    if (showFullDescription) {
      return "auto";
    }
    if (needsTruncation) {
      return 55;
    }
    return "auto";
  };

  const accordionItems = [
    {
      title: "Helps With",
      children: productConcerns ? (
        <div className="text-justify text-body2 text-primary">
          {productConcerns}
        </div>
      ) : undefined,
      titleClassName: "font-serif text-h4 font-semibold text-primary",
    },
    {
      title: "Used On",
      children: productAreas ? (
        <div className="text-justify text-body2 text-primary">
          {productAreas}
        </div>
      ) : undefined,
      titleClassName: "font-serif text-h4 font-semibold text-primary",
    },
    {
      title: "Ingredients",
      children: ingredientsText ? (
        <div className="text-justify text-body2 text-primary">
          {ingredientsText}
        </div>
      ) : undefined,
      titleClassName: "font-serif text-h4 font-semibold text-primary",
    },
  ].filter((item) => item.children);

  return (
    <>
      {keyBenefits.length > 0 && (
        <div className="p-4">
          <div className="text-h4 mb-2 font-serif font-semibold text-primary">
            Key Benefits
          </div>
          <div className="flex flex-col gap-1 text-body2">
            {keyBenefits.map((benefit, index) => (
              <div
                key={`benefit-${index}`}
                className="flex flex-row items-center gap-2 "
              >
                <Icon
                  svg={CheckIcon}
                  size="x2small"
                  color="brandColor"
                  className="mt-0.5 flex-shrink-0"
                />
                <span className="flex-1">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="flex flex-col gap-2">
          <div className="text-h4 font-serif font-semibold text-primary">
            Description
          </div>
          <div className="mb-2 text-body2">
            <AnimateHeight
              height={getDescriptionHeight()}
              duration={300}
              easing="ease-in-out"
            >
              <div className="break-words">{description.text}</div>
            </AnimateHeight>
          </div>
          {needsTruncation && (
            <button
              onClick={() => setShowFullDescription(!showFullDescription)}
              className="flex flex-col items-center"
            >
              {showFullDescription && (
                <Icon svg={ChevronLeft} size="small" className="rotate-90" />
              )}
              <span className="text-bold2">
                {showFullDescription ? "Show less" : "Show more"}
              </span>
              {!showFullDescription && (
                <Icon svg={ChevronRight} size="small" className="rotate-90" />
              )}
            </button>
          )}
        </div>
      </div>

      {accordionItems.length > 0 && (
        <div className="-mx-2">
          <AccordionList items={accordionItems} />
        </div>
      )}
    </>
  );
};
